/**
 * Notification React Query Hooks
 *
 * Custom hooks for managing notifications state and mutations
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery, QueryClient } from '@tanstack/react-query';
import { toast } from '@/utils/toast';
import { notificationService } from '@/services/notification.service';
import { Notification, NotificationScope, UpdateNotificationPreferencesRequest, CreatePushSubscriptionRequest } from '@/types/notification';
import { teamQueryKeys, portfolioNotificationKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useAuth } from '@/hooks/useAuth';
import { formatApiError } from '@/services/api';

// ===========================
// Query Keys
// ===========================

/**
 * Legacy notification keys - DEPRECATED
 * Use teamQueryKeys.notifications instead for team-scoped caching
 *
 * Kept for reference only. All new code should use teamQueryKeys.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters: { unreadOnly?: boolean }) => [...notificationKeys.lists(), filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
  vapidKey: () => [...notificationKeys.all, 'vapid-key'] as const,
};

/**
 * The bell is portfolio-scoped when the user is agency staff who is NOT inside a
 * brand (no God Mode session). Inside a brand (incl. God Mode) it is that brand only.
 */
export const useNotificationScope = (): NotificationScope => {
  const { user } = useAuth();
  return user?.isAgencyStaff === true && !user?.godModeSession ? 'portfolio' : 'team';
};

// ===========================
// Query Hooks
// ===========================

/**
 * Hook to fetch notifications with pagination
 * Uses team-scoped query keys for proper cache isolation in God Mode
 */
export const useNotifications = (page = 1, limit = 20, unreadOnly = false) => {
  const { teamId } = useEffectiveTeamId();
  const scope = useNotificationScope();
  const filters = { page, limit, isRead: unreadOnly ? false : undefined };

  return useQuery({
    queryKey:
      scope === 'portfolio'
        ? portfolioNotificationKeys.list(filters)
        : teamQueryKeys.notifications.list(teamId, filters),
    queryFn: () => notificationService.getNotifications(page, limit, unreadOnly, scope),
    // SSE handles real-time updates via cache invalidation
    staleTime: 30_000, // SSE + cross-tab sync invalidate; focus refetch is the safety net
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

/**
 * Hook to fetch notifications with infinite scroll
 * Uses team-scoped query keys for proper cache isolation in God Mode
 */
export const useInfiniteNotifications = (limit = 20, unreadOnly = false) => {
  const { teamId } = useEffectiveTeamId();
  const scope = useNotificationScope();
  const filters = { limit, isRead: unreadOnly ? false : undefined };

  return useInfiniteQuery({
    queryKey:
      scope === 'portfolio'
        ? portfolioNotificationKeys.list({ ...filters, infinite: true })
        : [...teamQueryKeys.notifications.list(teamId, filters), 'infinite'],
    queryFn: ({ pageParam = 1 }) =>
      notificationService.getNotifications(pageParam, limit, unreadOnly, scope),
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      if (pagination.page < pagination.pages) {
        return pagination.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    // SSE handles real-time updates via cache invalidation
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

/**
 * Hook to fetch unread notification count
 * Used for notification badge
 * Uses team-scoped query keys for proper cache isolation in God Mode
 */
export const useUnreadNotificationCount = () => {
  const { teamId } = useEffectiveTeamId();
  const scope = useNotificationScope();

  return useQuery({
    queryKey:
      scope === 'portfolio'
        ? portfolioNotificationKeys.unreadCount()
        : teamQueryKeys.notifications.unreadCount(teamId),
    queryFn: () => notificationService.getUnreadCount(scope),
    // SSE handles real-time updates via cache invalidation
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

/**
 * Hook to fetch notification preferences
 * Uses team-scoped query keys for proper cache isolation in God Mode
 */
export const useNotificationPreferences = () => {
  const { teamId } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.notifications.preferences(teamId),
    queryFn: notificationService.getPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch VAPID public key
 */
export const useVapidPublicKey = () => {
  return useQuery({
    queryKey: notificationKeys.vapidKey(),
    queryFn: notificationService.getVapidPublicKey,
    staleTime: Infinity, // VAPID key doesn't change
    gcTime: Infinity, // Keep in cache forever (renamed from cacheTime in v5)
  });
};

// ===========================
// Mutation Hooks
// ===========================

const invalidateAll = (queryClient: QueryClient, teamId: number | null): void => {
  queryClient.invalidateQueries({ queryKey: teamQueryKeys.notifications.all(teamId) });
  queryClient.invalidateQueries({ queryKey: portfolioNotificationKeys.all });
};

/**
 * Hook to mark a notification as read
 * Uses team-scoped query keys for proper cache invalidation in God Mode
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (notificationId: string) => notificationService.markAsRead(notificationId),
    onSuccess: () => {
      // Invalidate all notification queries to refetch fresh data
      invalidateAll(queryClient, teamId);
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Hook to mark all notifications as read
 * Uses team-scoped query keys for proper cache invalidation in God Mode
 */
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  const scope = useNotificationScope();

  const rootKey =
    scope === 'portfolio' ? portfolioNotificationKeys.all : teamQueryKeys.notifications.all(teamId);
  const countKey =
    scope === 'portfolio'
      ? portfolioNotificationKeys.unreadCount()
      : teamQueryKeys.notifications.unreadCount(teamId);

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(scope),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: rootKey });

      const previousCount = queryClient.getQueryData<number>(countKey);

      const listQueriesData = queryClient.getQueriesData<{
        notifications: Notification[];
        pagination: { page: number; limit: number; total: number; pages: number };
      }>({
        queryKey: [...rootKey, 'list'],
      });

      listQueriesData.forEach(([queryKey, data]) => {
        if (data?.notifications) {
          queryClient.setQueryData(queryKey, {
            ...data,
            notifications: data.notifications.map((n) => ({
              ...n,
              isRead: true,
              readAt: n.readAt || new Date().toISOString(),
            })),
          });
        }
      });

      queryClient.setQueryData(countKey, 0);

      return { listQueriesData, previousCount };
    },

    onError: (_err, _variables, context) => {
      if (context?.listQueriesData) {
        context.listQueriesData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(countKey, context.previousCount);
      }
    },

    onSettled: () => {
      invalidateAll(queryClient, teamId);
    },
  });
};

/**
 * Hook to delete a notification
 * Uses team-scoped query keys for proper cache invalidation in God Mode
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (notificationId: string) => notificationService.deleteNotification(notificationId),
    onSuccess: () => {
      invalidateAll(queryClient, teamId);
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Hook to update notification preferences
 * Uses team-scoped query keys for proper cache invalidation in God Mode
 */
export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (preferences: UpdateNotificationPreferencesRequest) =>
      notificationService.updatePreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.notifications.preferences(teamId) });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Hook to subscribe to push notifications
 */
export const useSubscribeToPush = () => {
  return useMutation({
    mutationFn: (subscription: CreatePushSubscriptionRequest) =>
      notificationService.subscribeToPush(subscription),
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};

/**
 * Hook to unsubscribe from push notifications
 */
export const useUnsubscribeFromPush = () => {
  return useMutation({
    mutationFn: (subscriptionId: string) => notificationService.unsubscribeFromPush(subscriptionId),
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
};
